import { defineStore } from 'pinia';
import { useUserStore, USER_SETTINGS_KEYS } from '@/stores/user';
import { useAccountStore } from '@/stores/account';
import { useProductsStore } from '@/stores/products';
import { fetchSubscription } from "@/services/ppApi";
import { gaEvent } from '@/utils/analytics/prospectpro';

export const useOrderStore = defineStore('order', {
    state: () => {
        const accountStore = useAccountStore();
        const accountId = accountStore?.account?.account_id ? accountStore.account.account_id : null;
        const userStore = useUserStore();
        const orderSettings = userStore.getSettings(USER_SETTINGS_KEYS.order, accountId || null);

        return {
            order: orderSettings.order || null,
            step: orderSettings.step || 1,
            subscriptionType: orderSettings.subscriptionType || accountStore.productType || null,
            coupon: orderSettings.coupon || {},
            invoiceDetailsModified: false,
            loading: false,
            defaultSteps: [
                {
                    key: "subscription",
                    name: "views.account_order.steps.subscription.name"
                },
                {
                    key: "invoice_details",
                    name: "views.account_order.steps.invoice_details.name"
                },
                {
                    key: "summary",
                    name: "views.account_order.steps.summary.name"
                }
            ]
        }
    },
    getters: {
        steps() {
            return this.defaultSteps.map((step, index) => ({
                ...step,
                current: this.step === index + 1 ? true : false,
                clickable: this.validateStepClickable(step.key),
                completed: this.validateStep(step.key),
                order: this.order
            }));
        },
        currentStep() {
            return this.steps[this.step - 1] || false;
        },
        newSubscriptionSelected() {
            const accountStore = useAccountStore();
            const subscriptions = accountStore.subscriptions;

            if(subscriptions?.length > 0) {
                return (subscriptions[0].product_main !== this.subscriptionType && subscriptions[0].status !== 'canceled') ||
                subscriptions[0].status === 'canceled';
            } else if (this.subscriptionType && (process.env.VUE_APP_PRODUCT_FREE ? this.subscriptionType !== process.env.VUE_APP_PRODUCT_FREE : this.subscriptionType !== 'free') && (process.env.VUE_APP_PRODUCT_TRIAL ? this.subscriptionType !== process.env.VUE_APP_PRODUCT_TRIAL: this.subscriptionType !== 'trial')) {
                return true;
            } else {
                return false;
            }
        },
        activeDiscount() {
            return this.coupon?.discount > 0 ? this.coupon.discount : 0;
        }
    },
    actions: {
        resetOrder() {
            this.$reset();
        },
        saveOrderSettings(deleteSettings = false) {
            const userStore = useUserStore();
            const accountStore = useAccountStore();

            userStore.saveSettings(
                USER_SETTINGS_KEYS.order,
                accountStore.account?.account_id || null,
                deleteSettings ? null : {
                    subscription: this.subscription,
                    subscriptionType: this.subscriptionType,
                    coupon: this.coupon,
                    step: this.step,
                    order: this.order
                }
            );
        },
        findStep(key) {
            const index = this.steps.findIndex(step => step.key === key);
            return index + 1;
        },
        navigateToStep(step) {
            if(typeof step === 'number') {
                this.step = step;
            } else if (typeof step === 'string' && step !== '') {
                this.step = this.findStep(step) || this.step;
            }
            this.invoiceDetailsModified = false;
        },
        validateStepClickable(key) {
            const accountStore = useAccountStore();

            switch (key) {
                case 'subscription' :
                    return true;
                case 'invoice_details':
                    return this.newSubscriptionSelected;
                case 'summary':
                    return this.newSubscriptionSelected && accountStore.hasInvoiceAddress;
                default:
                    return false;
            }
        },
        validateStep(key) {
            const accountStore = useAccountStore();

            switch (key) {
                case 'subscription' :
                    return this.newSubscriptionSelected;
                case 'invoice_details':
                    return this.newSubscriptionSelected && accountStore.hasInvoiceAddress;
                case 'summary':
                    return false;
                default:
                    return false;
            }
        },
        setCoupon(coupon) {
            if(parseFloat(coupon.discount) > 0) {
                this.coupon = coupon;
            } else {
                this.coupon = {};
            }
        },
        setSubscription(subscriptionType) {
            this.updateSubscription(subscriptionType);
            this.navigateToStep(2)
        },
        updateSubscription(subscriptionType) {
            this.subscriptionType = subscriptionType;
        },
        setInvoiceDetails() {
            this.invoiceDetailsModified = false;
            this.navigateToStep(3);
        },
        updateInvoiceDetailsModified(val) {
            this.invoiceDetailsModified = val;
        },
        generateProduct() {
            return this.subscriptionType ? useProductsStore().getProduct(this.subscriptionType).product || `${process.env.VUE_APP_PRODUCT_PREFIX || 'bd'}_${this.subscriptionType}_v1` : 'bd_free_v1';
        },
        async startPayment() {
            try {
                const response = await useAccountStore().createSubscription({
                    product: this.generateProduct(),
                    ...(this.coupon?.coupon && { coupon: this.coupon.coupon })
                });
                this.order = response.data;
                return true;
            } catch {
                return false;
            } finally {
                this.saveOrderSettings();
                this.redirectPayment();
            }
        },
        async redirectPayment() {
            if(this.order?.order_id && this.order?.checkout_url) {
                window.location.href = this.order.checkout_url;

                gaEvent('begin_checkout', true, {
                    ...(this.order?.price && { value: this.order.price }),
                    ...(this.order?.order_id && { transaction_id: this.order.order_id })
                });
            }
        },
        async fetchSubscription(order_id) {
            this.loading = true;

            try {
                const response = await fetchSubscription(order_id, useAccountStore().account.account_id);
                return response?.data?.subscriptions?.length > 0 ? response.data.subscriptions : false;
            } catch {
                return false;
            } finally {
                this.loading = false;
            }
        },
        async validateOrderStatus(order_id) {
            const orders = await this.fetchSubscription(order_id);
            const order = Object.values(orders).find(order => order?.order_id === order_id);
            const orderPaid = order?.status === 'paid';

            if(orderPaid) {
                return order;
            } else {
                return false;
            }
        },
    },
});
